import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import type { MyReport } from '@/lib/feedback/api';

/* SPEC-506 — "My feedback": every report this login filed, its status, the operator's
 * reply and, once fixed, the release it shipped in (and the release note's date). A dot marks a
 * report whose status changed since the list was last opened.
 * SPEC-521: the Scenarios page's "My requests" renders this same list (one store, one list). */

const STATUS_TONE: Record<string, string> = {
  received: 'text-[var(--text-tertiary)] border-[var(--border-default)]',
  looking_into_it: 'text-blue-300 border-blue-400/40',
  need_more_from_you: 'text-amber-300 border-amber-400/50',
  fixed: 'text-green-300 border-green-400/40',
  not_doing: 'text-[var(--text-secondary)] border-[var(--border-default)]',
  answered: 'text-green-300 border-green-400/40',
};

function when(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/** A release date (YYYY-MM-DD) as "7 Oct 2026"; read as a calendar date, never shifted by time zone. */
export function releasedOn(ymd: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd ?? '');
  if (!m) return '';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** " — live since 3 Oct 2026" when shipping is proved (a merge reachable from main), else
 *  " — released 3 Oct 2026" from the release note, else nothing (SPEC-521 fix loop 1). */
function fixedWhen(f: { released_on?: string | null; live_since?: string | null }): string {
  if (releasedOn(f.live_since)) return ` — live since ${releasedOn(f.live_since)}`;
  if (releasedOn(f.released_on)) return ` — released ${releasedOn(f.released_on)}`;
  return '';
}

export function MyFeedback({ items, unreadIds, onNavigate }: { items: MyReport[]; unreadIds: string[]; onNavigate: () => void }) {
  if (items.length === 0) {
    return <p className="text-sm text-[var(--text-tertiary)] py-6 text-center">You have not sent any feedback yet.</p>;
  }
  return (
    <ul className="space-y-2">
      {items.map((r) => (
        <li key={r.id} className="p-3 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-base)]">
          <div className="flex items-center gap-2 text-[11px] text-[var(--text-tertiary)]">
            {unreadIds.includes(r.id) && <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0" aria-label="Status changed" />}
            <span>{r.category_label}</span>
            <span>·</span>
            <span>{when(r.submitted_at)}</span>
            <span className={`ml-auto px-1.5 py-0.5 rounded border text-[10px] font-medium ${STATUS_TONE[r.status] ?? STATUS_TONE.received}`}>
              {r.status_label}
            </span>
          </div>
          <p className="mt-1 text-sm text-[var(--text-primary)] whitespace-pre-wrap break-words">{r.text}</p>
          {r.filed_from && <p className="mt-0.5 text-[11px] text-[var(--text-tertiary)]">From: {r.filed_from}</p>}
          {r.reply && (
            <p className="mt-2 text-xs text-[var(--text-secondary)] border-l-2 border-amber-400/60 pl-2 whitespace-pre-wrap">
              {r.status === 'need_more_from_you' ? 'Question for you: ' : 'Reply: '}{r.reply}
            </p>
          )}
          {r.fixed_in && (
            r.fixed_in.link && r.fixed_in.title ? (
              <Link to={r.fixed_in.link} onClick={onNavigate}
                className="mt-2 inline-flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300">
                Fixed in “{r.fixed_in.title}”{fixedWhen(r.fixed_in)}
                <ExternalLink className="w-3 h-3" aria-hidden="true" />
              </Link>
            ) : releasedOn(r.fixed_in.live_since) ? (
              <p className="mt-2 text-xs text-[var(--text-secondary)]">Fixed — live since {releasedOn(r.fixed_in.live_since)}.</p>
            ) : (
              <p className="mt-2 text-xs text-[var(--text-tertiary)]">Fixed.</p>
            )
          )}
          {r.status === 'need_more_from_you' && (
            <p className="mt-1 text-[11px] text-[var(--text-tertiary)]">Answer by sending another note from the same page.</p>
          )}
        </li>
      ))}
    </ul>
  );
}
